import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { ConfettiPop } from "../ui/ConfettiPop";

interface CelebrationScreenProps {
  allDone: boolean;
  total: number;
  correctedCount: number;
}

/** The deck's all-caught-up screen, shown when nothing is left to review. */
export function CelebrationScreen({
  allDone,
  total,
  correctedCount,
}: CelebrationScreenProps): ReactNode {
  return (
    <motion.div
      className="celebration-screen"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      <div className="celebration-confetti-anchor">
        <ConfettiPop trigger={allDone} />
      </div>
      <motion.div
        className="celebration-checkmark"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 15,
          delay: 0.1,
        }}
      >
        <svg viewBox="0 0 52 52" width="64" height="64">
          <circle
            cx="26"
            cy="26"
            r="24"
            style={{
              fill: "var(--color-success-bg)",
              stroke: "var(--color-success)",
            }}
            strokeWidth="2"
          />
          <motion.path
            d="M14 27l8 8 16-18"
            fill="none"
            style={{ stroke: "var(--color-success)" }}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          />
        </svg>
      </motion.div>
      <h3 className="celebration-title">All caught up!</h3>
      <div className="celebration-stats">
        <span>
          {total} transactions categorized
          {correctedCount > 0 && `, ${correctedCount} corrected`}
        </span>
      </div>
      {/* Progress ring at 100% */}
      <div className="celebration-ring">
        <svg viewBox="0 0 36 36" width="80" height="80">
          <circle
            cx="18"
            cy="18"
            r="15.91"
            fill="none"
            style={{ stroke: "var(--color-border)" }}
            strokeWidth="2.5"
          />
          <motion.circle
            cx="18"
            cy="18"
            r="15.91"
            fill="none"
            style={{ stroke: "var(--color-success)" }}
            strokeWidth="2.5"
            strokeDasharray="100"
            strokeLinecap="round"
            initial={{ strokeDashoffset: 100 }}
            animate={{ strokeDashoffset: 0 }}
            transition={{
              type: "spring",
              stiffness: 60,
              damping: 15,
              delay: 0.2,
            }}
          />
        </svg>
        <span className="celebration-ring-label">100%</span>
      </div>
    </motion.div>
  );
}
